import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, Lock, MessageCircle, MessageSquarePlus,
  Pin, Search,
} from "lucide-react";

import { NewThreadDialog } from "@/components/NewThreadDialog";
import { OnlineUsersBadge } from "@/components/OnlineUsersBadge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useThreads } from "@/hooks/useForum";
import { formatDateTime, initials } from "@/lib/format";
import type { ThreadCategory } from "@/lib/types";
import { cn } from "@/lib/utils";

const CATEGORIES: { value: ThreadCategory | ""; label: string }[] = [
  { value: "",                label: "All categories" },
  { value: "general",         label: "General" },
  { value: "announcement",    label: "Announcement" },
  { value: "kpi",             label: "KPI" },
  { value: "verification",    label: "Verification" },
  { value: "climate_finance", label: "Climate Finance" },
  { value: "carbon_market",   label: "Carbon Market" },
  { value: "resources",       label: "Resources" },
];

const PAGE_SIZE = 20;

/** /forum — list of threads, server-sorted (pinned first, then by
 *  last activity). Filter by category + search title/body. */
export default function Forum() {
  const [search,      setSearch]      = useState("");
  const [searchDraft, setSearchDraft] = useState("");
  const [category,    setCategory]    = useState<ThreadCategory | "">("");
  const [page,        setPage]        = useState(1);
  const [newOpen,     setNewOpen]     = useState(false);

  const { data, isLoading, isFetching } = useThreads({
    search: search || undefined, category: category || undefined, page,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-emerald-700 text-primary-foreground shadow-soft">
            <MessageCircle className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Forum</h1>
            <p className="text-sm text-muted-foreground">
              Discussion threads — ask, share, decide together.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <OnlineUsersBadge />
          <Button onClick={() => setNewOpen(true)}>
            <MessageSquarePlus className="mr-2 h-4 w-4" />New thread
          </Button>
        </div>
      </header>

      <NewThreadDialog open={newOpen} onOpenChange={setNewOpen} />

      {/* Filters */}
      <Card className="border-border/60 shadow-soft">
        <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center">
          <form
            className="relative flex-1"
            onSubmit={(e) => { e.preventDefault(); setSearch(searchDraft.trim()); setPage(1); }}
          >
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search title or body…"
              className="h-10 pl-9"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
            />
          </form>
          <select
            value={category}
            onChange={(e) => { setCategory(e.target.value as ThreadCategory | ""); setPage(1); }}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm font-medium"
          >
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </CardContent>
      </Card>

      {/* Thread list */}
      <Card className="overflow-hidden border-border/60 shadow-soft">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-20 w-full" />)}
            </div>
          ) : data && data.results.length > 0 ? (
            <ul className="divide-y divide-border/60">
              {data.results.map((t) => (
                <li key={t.id} className="px-4 py-3 transition-colors hover:bg-muted/30 sm:px-5 sm:py-4">
                  <div className="flex items-start gap-3">
                    <Avatar className="h-9 w-9 shrink-0 ring-1 ring-border">
                      <AvatarFallback className="bg-gradient-to-br from-primary to-emerald-700 text-[11px] font-semibold text-primary-foreground">
                        {initials(t.author_name || "")}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        {t.is_pinned && (
                          <Pin className="h-3.5 w-3.5 fill-amber-400 text-amber-500" />
                        )}
                        {t.is_locked && (
                          <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                        )}
                        <Link
                          to={`/forum/threads/${t.id}`}
                          className={cn(
                            "text-sm hover:text-primary hover:underline",
                            // Unread threads pop visually — bolder weight
                            // + foreground colour, like the inbox treatment
                            // in every chat app.
                            t.unread_count > 0 ? "font-bold text-foreground" : "font-semibold",
                          )}
                        >
                          {t.title}
                        </Link>
                        {t.unread_count > 0 && (
                          <span
                            className="ml-auto inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-bold leading-none text-primary-foreground"
                            title={`${t.unread_count} new ${t.unread_count === 1 ? "reply" : "replies"}`}
                            aria-label={`${t.unread_count} unread`}
                          >
                            {t.unread_count > 99 ? "99+" : t.unread_count}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                        <Badge variant="outline" className={cn(categoryClass(t.category), "font-medium")}>
                          {t.category_display}
                        </Badge>
                        <span>by <span className="font-medium text-foreground">{t.author_name || "—"}</span></span>
                        <span>·</span>
                        <span>last activity {formatDateTime(t.last_post_at)}</span>
                      </div>
                    </div>
                    <div className="hidden shrink-0 text-right text-xs text-muted-foreground sm:block">
                      <div className="font-mono text-lg font-bold tabular-nums text-foreground">
                        {t.post_count.toLocaleString()}
                      </div>
                      reply{t.post_count === 1 ? "" : "s"}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-6 py-16 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <MessageCircle className="h-5 w-5 text-muted-foreground" />
              </div>
              <h3 className="text-sm font-semibold">No threads {search || category ? "match" : "yet"}</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                {search || category
                  ? "Try clearing the filter."
                  : "Be the first to start a discussion."}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {data && data.count > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">
            <span className="font-mono font-semibold tabular-nums">{data.count.toLocaleString()}</span>{" "}
            thread{data.count === 1 ? "" : "s"}
            {isFetching && <span className="ml-2 text-xs">loading…</span>}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={!data.previous}
              onClick={() => setPage((p) => Math.max(1, p - 1))}>
              <ChevronLeft className="h-4 w-4" />Previous
            </Button>
            <div className="rounded-md border bg-card px-3 py-1.5 text-sm font-mono tabular-nums text-muted-foreground">
              {page} / {totalPages}
            </div>
            <Button variant="outline" size="sm" disabled={!data.next}
              onClick={() => setPage((p) => p + 1)}>
              Next<ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Each category gets its own subtle tinted badge — scannable across the
// list without becoming visual noise.
function categoryClass(c: ThreadCategory): string {
  switch (c) {
    case "announcement":    return "bg-rose-50 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-500/30";
    case "kpi":             return "bg-amber-50 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/30";
    case "verification":    return "bg-violet-50 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-500/30";
    case "climate_finance": return "bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30";
    case "carbon_market":   return "bg-blue-50 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/30";
    case "resources":       return "bg-orange-50 text-orange-700 border-orange-200";
    default:                return "bg-muted/40 text-foreground/80 border-border";
  }
}

